import { useState, useCallback } from 'react';
import { CheckCircleIcon, XCircleIcon } from '@heroicons/react/24/outline';
import clsx from 'clsx';

type ToastType = 'success' | 'error';

interface ToastMessage {
  id: number;
  type: ToastType;
  message: string;
}

let toastIdCounter = 0;
let externalAddToast: ((type: ToastType, message: string) => void) | null = null;

export function toast(type: ToastType, message: string) {
  externalAddToast?.(type, message);
}

export function ToastContainer() {
  const [toasts, setToasts] = useState<ToastMessage[]>([]);

  const addToast = useCallback((type: ToastType, message: string) => {
    const id = ++toastIdCounter;
    setToasts((prev) => [...prev, { id, type, message }]);
    setTimeout(() => setToasts((prev) => prev.filter((t) => t.id !== id)), 4000);
  }, []);

  externalAddToast = addToast;

  return (
    <div className="fixed bottom-4 right-4 z-50 flex flex-col gap-2 w-80" aria-live="polite">
      {toasts.map((t) => (
        <div
          key={t.id}
          role="alert"
          className={clsx('flex items-center gap-3 rounded-lg p-4 shadow-lg text-sm', {
            'bg-green-50 text-green-800': t.type === 'success',
            'bg-red-50 text-red-800': t.type === 'error',
          })}
        >
          {t.type === 'success' ? (
            <CheckCircleIcon className="h-5 w-5 shrink-0 text-green-500" />
          ) : (
            <XCircleIcon className="h-5 w-5 shrink-0 text-red-500" />
          )}
          <span>{t.message}</span>
        </div>
      ))}
    </div>
  );
}
